import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ZCmsDocumentSelect, ZCmsDocumentStatus, ZCmsTypeSlug } from './models';

const c = initContract();

const ZNotFound = z.object({ message: z.string() });
const ZBadRequest = z.object({ message: z.string() });

// ─── Contract ─────────────────────────────────────────────────────────────────

export const adminCmsContract = c.router(
  {
    // ── Documents ──────────────────────────────────────────────────────────────

    listDocuments: {
      method: 'GET',
      path: '/types/:typeSlug/documents',
      pathParams: z.object({ typeSlug: ZCmsTypeSlug }),
      query: z.object({
        status: ZCmsDocumentStatus.optional(),
      }),
      responses: {
        200: ZCmsDocumentSelect.array(),
        400: ZBadRequest,
      },
      summary: 'List documents for a CMS type',
    },

    createDocument: {
      method: 'POST',
      path: '/types/:typeSlug/documents',
      pathParams: z.object({ typeSlug: ZCmsTypeSlug }),
      body: z.object({
        data: z.record(z.unknown()),
        status: ZCmsDocumentStatus.optional(),
        sortOrder: z.number().int().optional(),
      }),
      responses: {
        201: ZCmsDocumentSelect,
        400: ZBadRequest,
      },
      summary: 'Create a document for a CMS type',
    },

    getDocument: {
      method: 'GET',
      path: '/types/:typeSlug/documents/:id',
      pathParams: z.object({ typeSlug: ZCmsTypeSlug, id: z.coerce.number() }),
      responses: {
        200: ZCmsDocumentSelect,
        400: ZBadRequest,
        404: ZNotFound,
      },
      summary: 'Get a CMS document by ID',
    },

    updateDocument: {
      method: 'PUT',
      path: '/types/:typeSlug/documents/:id',
      pathParams: z.object({ typeSlug: ZCmsTypeSlug, id: z.coerce.number() }),
      body: z.object({
        data: z.record(z.unknown()).optional(),
        status: ZCmsDocumentStatus.optional(),
        sortOrder: z.number().int().optional(),
      }),
      responses: {
        200: ZCmsDocumentSelect,
        400: ZBadRequest,
        404: ZNotFound,
      },
      summary: 'Update a CMS document',
    },

    deleteDocument: {
      method: 'DELETE',
      path: '/types/:typeSlug/documents/:id',
      pathParams: z.object({ typeSlug: ZCmsTypeSlug, id: z.coerce.number() }),
      body: c.noBody(),
      responses: {
        200: z.object({ success: z.boolean() }),
        400: ZBadRequest,
        404: ZNotFound,
      },
      summary: 'Delete a CMS document',
    },

    // ── Image upload ───────────────────────────────────────────────────────────

    uploadImage: {
      method: 'POST',
      path: '/upload-image',
      contentType: 'multipart/form-data',
      body: c.type<{ image: File }>(),
      responses: {
        200: z.string(),
        400: ZBadRequest,
      },
      summary: 'Upload a CMS image to S3',
    },
  },
  {
    pathPrefix: '/admin',
    strictStatusCodes: true,
    commonResponses: {
      401: c.type<{ message: string; statusCode: 401 }>(),
      403: c.type<{ message: string; statusCode: 403 }>(),
    },
  },
);
